import React, { useMemo, useState } from "react";
import { ValeurChamp } from "./BibValeur";

// ===================================================================
// The Crowd v2 — Bibliotheques : tableau de bord, referentiel technique
//
// Composants de presentation : les lignes sont chargees par la page
// (services/bibliotheque.js). Le tableau de bord suit _config.json
// (colonnes, filtres, versioning) ; sans colonnes configurees, il
// affiche la vue « procedures » (titre, date, auteur).
// ===================================================================

export function TableauDeBord({ lignes, loading, config, onSelectFile }) {
  const [filtres, setFiltres] = useState({});

  const colonnes = config?.dashboard?.colonnes || [];
  const clesFiltres = config?.dashboard?.filtres || [];
  const champs = config?.front_matter || [];
  const versioning = config?.versioning !== false;

  const champ = (cle) => champs.find((f) => f.cle === cle) || { cle, label: cle, type: "text" };

  const options = useMemo(() => {
    const o = {};
    for (const f of clesFiltres) o[f] = [...new Set(lignes.map((p) => p[f]).filter(Boolean))];
    return o;
  }, [lignes, clesFiltres]);

  const filtrees = useMemo(
    () => lignes.filter((p) => Object.entries(filtres).every(([k, v]) => !v || p[k] === v)),
    [lignes, filtres]
  );

  if (loading) return <div className="v2-chargement">Chargement du tableau de bord…</div>;

  return (
    <section className="v2-bib-tableau" aria-labelledby="v2-bib-tdb-titre">
      <div className="v2-bloc-entete">
        <div>
          <h2 id="v2-bib-tdb-titre" className="v2-bloc-titre">Tableau de bord</h2>
          <p className="v2-bib-compte">{filtrees.length} fiche{filtrees.length > 1 ? "s" : ""}</p>
        </div>
        {clesFiltres.length > 0 && (
          <div className="v2-bib-filtres">
            {clesFiltres.map((f) => (
              <label key={f} className="v2-bib-filtre">
                <span className="v2-label">{champ(f).label}</span>
                <select value={filtres[f] || ""} onChange={(e) => setFiltres((v) => ({ ...v, [f]: e.target.value }))}>
                  <option value="">Tous</option>
                  {(options[f] || []).map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="v2-panneau v2-tableau-conteneur">
        <table className="v2-tableau v2-tableau-cliquable">
          <thead>
            <tr>
              {colonnes.length === 0 ? (
                <>
                  <th scope="col">Titre</th>
                  <th scope="col">Modifié le</th>
                  <th scope="col">Auteur</th>
                </>
              ) : (
                <>
                  {colonnes.map((c) => <th key={c} scope="col">{champ(c).label}</th>)}
                  {versioning && <th scope="col">Version</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {filtrees.length === 0 && (
              <tr><td colSpan={Math.max(colonnes.length + (versioning ? 1 : 0), 3)} className="v2-vide">Aucune fiche.</td></tr>
            )}
            {filtrees.map((p) => (
              <tr key={p.baseName} onClick={() => onSelectFile(p.relativePath)}>
                {colonnes.length === 0 ? (
                  <>
                    <td>
                      <button type="button" className="v2-lien-ligne" onClick={(e) => { e.stopPropagation(); onSelectFile(p.relativePath); }}>
                        {p.titre || p.baseName.replace(/_/g, " ")}
                      </button>
                    </td>
                    <td className="v2-cellule-discrete">{p.date || "—"}</td>
                    <td className="v2-cellule-discrete">{p.auteur || "—"}</td>
                  </>
                ) : (
                  <>
                    {colonnes.map((c, i) => (
                      <td key={c}>
                        {i === 0 ? (
                          <button type="button" className="v2-lien-ligne" onClick={(e) => { e.stopPropagation(); onSelectFile(p.relativePath); }}>
                            <ValeurChamp type={champ(c).type} valeur={p[c]} compact />
                          </button>
                        ) : (
                          <ValeurChamp type={champ(c).type} valeur={p[c]} compact />
                        )}
                      </td>
                    ))}
                    {versioning && <td className="v2-cellule-discrete">v{p.version}</td>}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function TableauReferentiel({ lignes, loading, config, onSelectFile }) {
  const rt = config?.referentiel_technique || {};
  const colonnes = rt.colonnes || [];

  if (loading) return <div className="v2-chargement">Chargement du référentiel technique…</div>;

  return (
    <section className="v2-bib-tableau" aria-labelledby="v2-bib-ref-titre">
      <div className="v2-bloc-entete">
        <div>
          <h2 id="v2-bib-ref-titre" className="v2-bloc-titre">{rt.label || "Référentiel Technique"}</h2>
          <p className="v2-bib-compte">{lignes.length} fiche{lignes.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="v2-panneau v2-tableau-conteneur">
        <table className="v2-tableau v2-tableau-cliquable">
          <thead>
            <tr>
              <th scope="col">Projet</th>
              {colonnes.map((c) => <th key={c.cle} scope="col">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {lignes.map((r) => (
              <tr key={r.key} onClick={() => onSelectFile(r.relativePath)}>
                <td>
                  <button type="button" className="v2-lien-ligne" onClick={(e) => { e.stopPropagation(); onSelectFile(r.relativePath); }}>
                    {r.projet}
                  </button>
                </td>
                {colonnes.map((c) => (
                  <td key={c.cle} className="v2-cellule-discrete">{r[c.cle] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
